/* eslint-disable no-console */
import { useEffect, ReactElement, useState } from 'react';

import {
  InjectedModule,
  usePostman,
  createCustomContext,
  showcaseStatus,
  initialPostman,
} from '../../showcase-react';

import { DefaultError } from './DefaultError';
import { Skeleton } from './Skeleton';
import styles from './styles.module.scss';

interface ITopbarExternalModule {
  host: string;
  componentId: string;
  notRequireAuth?: boolean;
}

interface ITopbarEnv {
  mf: string;
  api: string;
  auth: string;
}
interface ITopbarProps {
  title: string;
  externalModules?: Record<string, ITopbarExternalModule>;
  authHandler?: (
    onTokenReady: (
      token: string,
      tokenType: string,
      refreshToken: () => Promise<string>,
    ) => void,
    clearAuth: () => void,
  ) => void;
  renderError?: (error: string | boolean, reload: () => void) => ReactElement;
  onlyLoadState?: boolean;
  host?: string;
  customEnv?: ITopbarEnv;
  customContext?: { router: { navigate: (to: string) => void } };
  isShowcaseApp?: boolean;
  options?: Record<string, any>;
}

const envResolver = async (host: string): Promise<ITopbarEnv> => {
  const result = await fetch(`${host}/topbar-manifest.json`);
  if (result.status === 200) {
    return result.json();
  }
  throw new Error(
    `Cannot fetch manifest, status: ${result.status} ${result.statusText}`,
  );
};

const exchangeToken = async (
  authURL: string,
  token: string,
  tokenType: string,
): Promise<string> => {
  const result = await fetch(
    `${authURL}/api/v1/auth/token/exchange/?provider=${tokenType}`,
    {
      method: 'post',
      body: JSON.stringify({ access_token: token }),
      headers: {
        'content-type': 'application/json',
      },
    },
  );
  if (result.status === 200) {
    return (await result.json()).access_token;
  }
  throw new Error(
    `Cannot exchange token: ${result.status} ${result.statusText}`,
  );
};

const customContextFallback = {
  router: { navigate: (to: string) => (window.location.href = to) },
  isFallback: true,
};

export const Topbar = ({
  title,
  authHandler,
  renderError,
  onlyLoadState,
  host,
  customEnv,
  externalModules,
  customContext,
  isShowcaseApp,
  options,
}: ITopbarProps): ReactElement => {
  useEffect(() => {
    if (!isShowcaseApp) {
      initialPostman('lke');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    /**
     * in case when Topbar used on non-schowcase apps
     * customer should provide customContext with navigate method
     */
    let usedCustomContext = customContext;
    if (showcaseStatus() !== 'connected' && !customContext?.router.navigate) {
      console.error(
        'No customContext.router.navigate provided! Using fallback instead.',
      );
      usedCustomContext = customContextFallback;
    }
    if (usedCustomContext) {
      createCustomContext(usedCustomContext);
    }
  }, [customContext]);
  const [env, setEnv] = useState<ITopbarEnv>();
  const [isEnvError, setIsEnvError] = useState(false);
  const [isEnvLoad, setIsEnvLoad] = useState(true);
  const [isTopbarLoaded, setIsTopbarLoaded] = useState(false);
  useEffect(() => {
    if (onlyLoadState) return;

    setIsEnvError(false);
    setIsEnvLoad(true);
    if (!host && !customEnv) {
      console.error('host or customEnvResolver props is not provided!');
      setIsEnvError(true);
      setIsEnvLoad(false);
    } else {
      (customEnv ? Promise.resolve(customEnv) : envResolver(host!))
        .then(newEnv => {
          setEnv(newEnv);
          setIsEnvLoad(false);
          setIsEnvError(false);
        })
        .catch(e => {
          console.error(e);
          setIsEnvError(true);
        });
    }
  }, [host, customEnv, onlyLoadState]);
  const postman = usePostman();

  useEffect(() => {
    const handlers = postman?.accept('scp/topbar/loaded', () => {
      setIsTopbarLoaded(true);
    });
    return () => handlers?.remove();
  }, [postman]);

  useEffect(() => {
    if (!env || onlyLoadState) return;

    if (!env.mf) {
      console.error(
        `env is wrong (no mf value provided) ${JSON.stringify(env, null, 2)}`,
      );
      return;
    }

    postman?.send('topbar', {
      title,
      externalModules,
      api: env.api,
      env,
      customContext,
      options,
    });
  }, [
    postman,
    title,
    externalModules,
    env,
    onlyLoadState,
    isTopbarLoaded,
    options,
  ]);

  useEffect(() => {
    const clearAuth = (): void => {
      postman?.send('topbar-auth-clear', null);
    };
    const onTokenReady = async (
      token: string,
      tokenType: string,
      refreshToken: () => Promise<string>,
    ): Promise<void> => {
      if (!env || onlyLoadState) return;

      if (!env.auth) {
        console.error(
          `Authorization disabled: env is wrong (no auth value provided) ${JSON.stringify(
            env,
            null,
            2,
          )}`,
        );
        return;
      }
      const customRefreshToken = async (): Promise<string> => {
        if (typeof refreshToken !== 'function') {
          return Promise.reject('refreshToken should be a function!');
        }
        if (tokenType === 'eauth') {
          return refreshToken();
        }
        const newToken = await refreshToken();
        return exchangeToken(env.auth, newToken, tokenType);
      };
      let exchangeError = false;
      if (tokenType !== 'eauth') {
        try {
          token = await exchangeToken(env.auth, token, tokenType);
        } catch {
          exchangeError = true;
        }
      }
      if (!exchangeError) {
        postman?.send('topbar-auth', {
          token,
          refreshToken: customRefreshToken,
        });
      } else {
        postman?.send('topbar-exchange-error', () => {
          authHandler?.(onTokenReady, clearAuth);
        });
      }
    };
    authHandler?.(onTokenReady, clearAuth);
  }, [env, onlyLoadState]);
  const loadState = onlyLoadState ?? isEnvLoad;
  const showError = isEnvError || (env && !env.mf);
  return (
    <div className={styles.TopbarContainer}>
      {showError && <DefaultError />}
      {loadState && <Skeleton />}
      {!loadState && !showError && env && (
        <InjectedModule
          name='scp/topbar'
          host={env.mf}
          fallback={<Skeleton />}
          mode='open'
          errorElement={
            renderError
              ? renderError
              : (_err, reload) => <DefaultError onRetry={reload} />
          }
        />
      )}
    </div>
  );
};
